import React, { useState, useRef, useEffect } from 'react';

interface DatePickerProps {
    value: string; // YYYY-MM-DD
    onChange: (value: string) => void;
}

const MONTHS = [
    'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
    'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
];

const WEEKDAYS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];

export const DatePicker: React.FC<DatePickerProps> = ({ value, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const [year, month, day] = value.split('-').map(Number);
    const [viewDate, setViewDate] = useState(new Date(year, month - 1, 1));

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const daysInMonth = (y: number, m: number) => new Date(y, m + 1, 0).getDate();
    const firstDayOfMonth = (y: number, m: number) => new Date(y, m, 1).getDay();

    const handleDateClick = (d: number) => {
        const y = viewDate.getFullYear();
        const m = (viewDate.getMonth() + 1).toString().padStart(2, '0');
        const dayStr = d.toString().padStart(2, '0');
        onChange(`${y}-${m}-${dayStr}`);
        setIsOpen(false);
    };

    const changeMonth = (offset: number) => {
        setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + offset, 1));
    };

    const formatDateLabel = (val: string) => {
        const [y, m, d] = val.split('-');
        return `${d}/${m}/${y}`;
    };

    const renderDays = () => {
        const y = viewDate.getFullYear();
        const m = viewDate.getMonth();
        const days = [];
        const totalDays = daysInMonth(y, m);
        const firstDay = firstDayOfMonth(y, m);

        // Espaços vazios
        for (let i = 0; i < firstDay; i++) {
            days.push(<div key={`blank-${i}`} className="h-9" />);
        }

        // Dias do mês
        for (let i = 1; i <= totalDays; i++) {
            const isSelected = i === day && m === (month - 1) && y === year;
            const isToday = i === new Date().getDate() && m === new Date().getMonth() && y === new Date().getFullYear();

            days.push(
                <button
                    key={i}
                    onClick={() => handleDateClick(i)}
                    className={`
            h-9 w-9 rounded-xl text-[11px] font-bold transition-all flex items-center justify-center
            ${isSelected
                            ? 'bg-primary text-white shadow-lg shadow-primary/30 scale-110'
                            : 'text-slate-400 hover:bg-slate-700/40 hover:text-white'
                        }
            ${isToday && !isSelected ? 'text-primary border border-primary/20' : ''}
          `}
                >
                    {i}
                </button>
            );
        }
        return days;
    };

    return (
        <div className="relative" ref={containerRef}>
            <div
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-3 bg-slate-800/40 border border-slate-700/50 rounded-xl px-4 py-3 text-sm focus-within:ring-2 focus-within:ring-slate-500/50 focus-within:border-slate-500 outline-none transition-all text-slate-300 cursor-pointer hover:border-slate-600"
            >
                <span className="material-symbols-outlined text-slate-500 text-[20px]">calendar_today</span>
                <span className="flex-1 font-medium">{formatDateLabel(value)}</span>
            </div>

            {isOpen && (
                <div
                    className="absolute top-full mt-2 left-0 z-[60] w-72 border border-slate-700/60 rounded-2xl shadow-2xl p-4 animate-fade-in-up bg-slate-900"
                >
                    <div className="flex items-center justify-between mb-4">
                        <button onClick={() => changeMonth(-1)} className="p-1 hover:bg-slate-700/50 rounded-lg text-slate-400">
                            <span className="material-symbols-outlined">chevron_left</span>
                        </button>
                        <div className="text-center">
                            <p className="text-[10px] font-bold text-primary uppercase tracking-widest">{viewDate.getFullYear()}</p>
                            <p className="text-sm font-black text-white capitalize">{MONTHS[viewDate.getMonth()]}</p>
                        </div>
                        <button onClick={() => changeMonth(1)} className="p-1 hover:bg-slate-700/50 rounded-lg text-slate-400">
                            <span className="material-symbols-outlined">chevron_right</span>
                        </button>
                    </div>

                    <div className="grid grid-cols-7 gap-1 mb-2">
                        {WEEKDAYS.map(d => (
                            <div key={d} className="text-[9px] font-black text-slate-600 uppercase text-center py-1">{d}</div>
                        ))}
                    </div>

                    <div className="grid grid-cols-7 gap-1">
                        {renderDays()}
                    </div>
                </div>
            )}
        </div>
    );
};
